import { useMemo } from 'react';

import { useTranslation } from '@/i18n';

import { holidayService } from '@/api/domains/attendance';
import type { HolidayByRegionExportParams } from '@/api/domains/attendance/services/holiday.service';
import { useExport } from '@/hooks/ui/useExport';
import type { UseExportResult } from '@/hooks/ui/useExport';
import type { PaginatedListSort } from '@/hooks/ui/usePaginatedList';


export interface UseHolidayByRegionExportParams {
  searchText: string;
  sort: PaginatedListSort | undefined;
  year?: number | null;
  month?: number | null;
  regionId?: number | null;
  holidayTypeId?: number | null;
}

export type UseHolidayByRegionExportResult = UseExportResult;

export function useHolidayByRegionExport({
  searchText,
  sort,
  year,
  month,
  regionId,
  holidayTypeId,
}: UseHolidayByRegionExportParams): UseHolidayByRegionExportResult {
  const { t: translate } = useTranslation();

  const exportParams = useMemo<HolidayByRegionExportParams>(
    () => ({
      searchText: searchText || undefined,
      sort,
      year: year ?? undefined,
      month: month ?? undefined,
      regionId: regionId ?? undefined,
      holidayTypeId: holidayTypeId ?? undefined,
    }),
    [searchText, sort, year, month, regionId, holidayTypeId],
  );

  return useExport({
    exportFn: () => holidayService.exportByRegionFile(exportParams),
    successMessage: translate('attendance:holiday.messages.exportByRegionSuccess'),
    errorMessage: translate('attendance:holiday.messages.exportByRegionError'),
  });
}

export default useHolidayByRegionExport;
